import {
  Users, Server, AlertTriangle, ArrowRight, Filter, Search,
  TrendingUp, Calendar, Shield
} from "lucide-react";
import { getRatioPercentage } from "../../../lib/utils";
import { Button } from "../../../../../ui/common-ui/button";
import { Input } from "../../../../../ui/common-ui/input";

interface LifecycleData {
  stats: { total: number; active: number; retiring: number; deprecated: number };
  trend: number[];
  categories: { name: string; count: number; icon: any }[];
  alert: string;
  inventoryBreakdown?: any[];
  topDormant?: any[];
  managedBreakdown?: Record<string, any>;
}

interface ExpandedLifecycleCardProps {
  data: LifecycleData;
}

export const ExpandedLifecycleCard = ({ data }: ExpandedLifecycleCardProps) => {
  const { stats, categories, alert } = data;
  const activeWidth = getRatioPercentage(stats.active, stats.total);
  const retiringWidth = getRatioPercentage(stats.retiring, stats.total);
  const deprecatedWidth = getRatioPercentage(stats.deprecated, stats.total);

  const nhiInventory = data.inventoryBreakdown?.length ? data.inventoryBreakdown : [
    { name: "prod-api-gateway", type: "Service Account", status: "active", owner: "Platform Team", created: "2024-01-15", lastUsed: "2 hours ago", risk: "low" },
    { name: "ci-deploy-runner", type: "Service Account", status: "active", owner: "DevOps", created: "2023-06-22", lastUsed: "1 hour ago", risk: "medium" },
    { name: "legacy-batch-processor", type: "Service Account", status: "retiring", owner: "Unassigned", created: "2022-03-10", lastUsed: "45 days ago", risk: "high" },
    { name: "db-backup-automation", type: "API Key", status: "active", owner: "DBA Team", created: "2023-11-01", lastUsed: "12 hours ago", risk: "low" },
    { name: "analytics-ingestion", type: "OAuth App", status: "active", owner: "Data Team", created: "2024-02-01", lastUsed: "30 min ago", risk: "low" },
    { name: "deprecated-monitoring", type: "Service Account", status: "deprecated", owner: "Unassigned", created: "2021-08-15", lastUsed: "180 days ago", risk: "critical" },
  ];

  const lifecycleStages = [
    { stage: "Creation", count: 234, description: "New NHIs created this month" },
    { stage: "Active", count: stats.active, description: "Currently in use" },
    { stage: "Rotation", count: 567, description: "Scheduled for credential rotation" },
    { stage: "Retiring", count: stats.retiring, description: "Marked for retirement" },
    { stage: "Deprecated", count: stats.deprecated, description: "Pending deletion" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-sky-500/10">
          <Users className="w-4 h-4 text-sky-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-semibold text-[15px] text-foreground">NHI Lifecycle Registry</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-6 text-[11px]">
                <Filter className="w-3 h-3 mr-1" />
                Filter
              </Button>
              <Button size="sm" className="h-6 text-[11px]">
                <Users className="w-3 h-3 mr-1" />
                Add NHI
              </Button>
            </div>
          </div>
          <p className="text-[13px] text-muted-foreground">Complete inventory of non-human identities across all environments</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-sky-500/20">
          <div className="text-2xl font-bold text-foreground">{stats.total.toLocaleString()}</div>
          <p className="text-[13px] text-muted-foreground">Total Identities</p>
          <div className="flex items-center gap-1 mt-1 text-[11px] text-success">
            <TrendingUp className="w-2.5 h-2.5" />
            +12% this month
          </div>
        </div>

        <div className="p-4 rounded-xl bg-success/10 border border-success/20">
          <div className="text-2xl font-bold text-success">{stats.active.toLocaleString()}</div>
          <p className="text-[13px] text-muted-foreground">Active</p>
          <div className="mt-1 h-1.5 rounded-full bg-success/20 overflow-hidden">
            <div className="h-full bg-success" style={{ width: `${activeWidth}%` }} />
          </div>
        </div>

        <div className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/20">
          <div className="text-2xl font-bold text-sky-500">{stats.retiring}</div>
          <p className="text-[13px] text-muted-foreground">Retiring</p>
          <div className="mt-1 h-1.5 rounded-full bg-sky-500/20 overflow-hidden">
            <div className="h-full bg-sky-500" style={{ width: `${retiringWidth}%` }} />
          </div>
        </div>

        <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
          <div className="text-2xl font-bold text-primary">{stats.deprecated}</div>
          <p className="text-[13px] text-muted-foreground">Deprecated</p>
          <div className="mt-1 h-1.5 rounded-full bg-primary/20 overflow-hidden">
            <div className="h-full bg-primary" style={{ width: `${deprecatedWidth}%` }} />
          </div>
        </div>
      </div>

      {/* Alert Banner */}
      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-primary" />
          <span className="text-primary font-medium text-[13px]">{alert}</span>
        </div>
        <Button size="sm" variant="destructive" className="h-6 text-[11px]">
          Review Now
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>

      {/* Categories & Lifecycle Flow */}
      <div className="grid grid-cols-2 gap-6">
        {/* Categories */}
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h3 className="text-[15px] font-semibold mb-3 flex items-center gap-2">
            <Server className="w-4 h-4 text-sky-500" />
            Identity Categories
          </h3>
          <div className="space-y-2">
            {categories.map((cat) => (
              <div
                key={cat.name}
                className="flex items-center gap-3 p-3 rounded-lg bg-card hover:bg-muted/30 transition-all cursor-pointer group"
              >
                <div className="p-2 rounded-lg bg-sky-500/10">
                  <cat.icon className="w-4 h-4 text-sky-500" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-[13px]">{cat.name}</div>
                  <div className="text-[11px] text-muted-foreground">{getRatioPercentage(cat.count, stats.total).toFixed(1)}% of total</div>
                </div>
                <div className="text-lg font-bold text-foreground">{cat.count.toLocaleString()}</div>
                <ArrowRight className="w-3 h-3 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
            ))}
          </div>
        </div>

        {/* Lifecycle Flow */}
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h3 className="text-[15px] font-semibold mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-sky-500" />
            Lifecycle Stages
          </h3>
          <div className="relative">
            {lifecycleStages.map((stage, i) => (
              <div
                key={stage.stage}
                className="flex items-center gap-3 mb-3 last:mb-0"
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-[11px] ${i === 0 ? "bg-sky-500/20 text-sky-500" :
                    i === lifecycleStages.length - 1 ? "bg-primary/20 text-primary" :
                      "bg-muted text-muted-foreground"
                  }`}>
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[13px]">{stage.stage}</span>
                    <span className="text-[15px] font-bold">{stage.count.toLocaleString()}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{stage.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* NHI Table Preview */}
      <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold flex items-center gap-2">
            <Shield className="w-4 h-4 text-sky-500" />
            Recent NHI Activity
          </h3>
          <div className="relative w-48">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <Input placeholder="Search identities..." className="pl-7 h-7 text-[13px]" />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border/50">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Name</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Type</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Owner</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Last Used</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Risk</th>
              </tr>
            </thead>
            <tbody>
              {nhiInventory.map((nhi) => (
                <tr
                  key={nhi.name}
                  className="border-t border-border/30 hover:bg-muted/30 transition-colors cursor-pointer"
                >
                  <td className="p-2 font-medium text-[13px]">{nhi.name}</td>
                  <td className="p-2 text-[13px] text-muted-foreground">{nhi.type}</td>
                  <td className="p-2">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${nhi.status === "retiring" || nhi.status === "deprovisioned" ? "bg-primary/10 text-primary" : "bg-sky-500/10 text-sky-500"
                      }`}>{nhi.status}</span>
                  </td>
                  <td className="p-2 text-[13px] text-muted-foreground">{nhi.owner}</td>
                  <td className="p-2 text-[13px] text-muted-foreground">{nhi.lastUsed}</td>
                  <td className="p-2">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${nhi.risk === "critical" || nhi.risk === "high" ? "bg-primary/10 text-primary" : "bg-sky-500/10 text-sky-500"
                      }`}>{nhi.risk}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-center mt-3">
          <Button variant="outline" size="sm" className="text-[13px]">
            View All {stats.total.toLocaleString()} Identities
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};
